import useAsync from 'Hooks/useAsync';
import { getCategoriesApi } from 'API/tableAPI/groupAPI';
import { Group } from 'types/group';

export interface CategoryGroupFilter {
  label?: string;
  ids: number[];
  hasChildren: boolean;
}

const useCategoryGroups = (
  targetGroupName?: string,
  includeTrendingAndAll: boolean = true,
) => {
  return useAsync<CategoryGroupFilter[]>(
    async () => {
      const groups = await getCategoriesApi();

      const topLevelGroups = groups.filter((x) => !x.parentId);
      const trendingGroupIds = groups
        .filter((x) => x.isTopLevel)
        .map((x) => x.id);
      const targetGroup = groups.find((group) => group.name === targetGroupName);
      const targetSubGroups: Group[] = [];

      if (targetGroup) {
        // parentId's runtime representation vs. id's isn't fully pinned down by
        // the API — preserving the original loose-equality lookup here rather
        // than guessing a normalization that could change which groups match.
        const getSubGroups = (parentId: number) => {
          const subGroups = groups.filter(
            (group) => (group.parentId as unknown) === parentId,
          );
          subGroups.forEach((subGroup) => {
            targetSubGroups.push(subGroup);
            getSubGroups(subGroup.id);
          });
        };
        getSubGroups(targetGroup.id);
      }

      const groupFilters: CategoryGroupFilter[] = (
        targetGroup ? targetSubGroups : topLevelGroups
      ).map((topGroup) => ({
        label: topGroup.name,
        ids: groups
          .filter((subGroup) => (subGroup.parentId as unknown) === topGroup.id)
          .map((subGroup) => subGroup.id),
        hasChildren: groups.some(
          (subGroup) => (subGroup.parentId as unknown) === topGroup.id,
        ),
      }));

      const updatedFilters: CategoryGroupFilter[] = [];

      if (includeTrendingAndAll) {
        updatedFilters.push(
          { label: 'trending', ids: trendingGroupIds, hasChildren: true },
          {
            label: 'all',
            ids: groups.filter((g) => g.isLeaf).map((g) => g.id),
            hasChildren: true,
          },
        );
      }

      updatedFilters.push(...groupFilters);

      return updatedFilters;
    },
    { deps: [targetGroupName, includeTrendingAndAll] },
  );
};

export default useCategoryGroups;
